/* One whole lesson, on paper.

   The take-away used to be the five planning steps printed out: twenty-two
   pages that told a teacher how to arrive at a plan. The portal no longer
   teaches that, so the booklet cannot be assembled from it — and the thing it
   is assembled from instead is the point of the whole rebuild. A lesson is a
   goal in three levels and ten finished materials, and a booklet that says so
   without showing one is the old offer with new words on it.

   So this turns one lesson into sheets: what the lesson is, and then every
   material of it that goes on paper, one sheet each. The reader holds the
   finished thing before being asked whether they want it.

   Two kinds of sheet come out of it and both are generated rather than picked,
   because none of what they carry is a block anywhere. The goal is a field of
   the document and the kit is a list of items; a handout selector reaches
   blocks and nothing else, which is why `generate` exists.

   What does NOT come out of it: the prompts. Every kit item carries the prompt
   that makes it, and printing ten of them beside the finished materials would
   put the same prompts in the booklet twice — the five phases of the
   production sitting are already there, in their general form, which is the
   form a reader with a different topic needs. A sheet quotes; it does not
   restate.

   OWNED BY ITEM 13 OF PLAN.md. */

import type { GeneratedSheet, Pick } from "./handout";
import { GOAL_LEVELS, isAbsent, isLesson } from "./types";
import type { Block, Doc, KitItem, LessonDoc, Localized } from "./types";

type Text = (value: Localized | undefined) => string;
type Ui = (key: string) => string;

/* Which materials go on a sheet of their own.

   `print` is the field that says a material is meant for paper, and it is set
   only where the material is blocks. A recording is not, but its script is —
   it is text, it is read, it is corrected, and a booklet that dropped it would
   claim a lesson has eight materials. An application is neither: it is a file
   that runs, and a printed picture of one is not one. It keeps its row in the
   table of contents and gets no sheet. */
function printable(item: KitItem): boolean {
  if (isAbsent(item) || !item.ready) return false;
  return item.ready.as === "blocks" ? item.print === true : item.ready.as === "audio";
}

/* The levels a material serves, named. Short labels, because this is a cell of
   a four-column table and "Očekávaná úroveň" three times over is a column of
   the word "úroveň". */
function levels(item: KitItem, ui: Ui): string {
  if (isAbsent(item)) return "—";
  return GOAL_LEVELS.filter((l) => item.levels.includes(l))
    .map((l) => ui(`level.${l}.short`)).join(", ");
}

/* The sheet the section opens on: the goal, and under it the ten materials as
   the contents of the sheets that follow.

   The generic sheet earlier in the booklet says what the three levels are for
   and what has to be written in each; this one is the instance of that rule.
   Both belong — a rule with no example is advice, an example with no rule is
   somebody else's lesson.

   The two tables share a page because they are read together: which materials
   serve the level a teacher is worried about is a question asked of both at
   once, and the answer is in the last column of the second table. */
function headSheet(doc: LessonDoc, t: Text, ui: Ui): GeneratedSheet {
  const goal = {
    k: "table",
    title: ui("handout.goalOf"),
    head: [ui("handout.level"), ui("handout.can"), ui("lesson.proof")],
    /* The level and the pupil it is for share a cell: on paper a fourth column
       would be four words wide, and the two belong together anyway — a level
       is a name for a pupil, not for a difficulty. */
    rows: doc.goal.levels.map((level) => [
      `**${ui(`level.${level.id}.short`)}** — ${t(level.who)}`,
      t(level.can),
      t(level.proof),
    ]),
  } as unknown as Block;

  const kit = {
    k: "table",
    title: ui("handout.kitOf"),
    lead: ui("handout.kitOfLead"),
    head: [ui("handout.material"), ui("handout.shape"),
           ui("lesson.minutes"), ui("handout.levels")],
    rows: doc.kit.map((item) => [
      ui(`kit.${item.kind}`),
      isAbsent(item) ? `${ui("lesson.absent")} — ${t(item.absent)}` : t(item.title),
      isAbsent(item) || !item.minutes ? "—" : String(item.minutes),
      levels(item, ui),
    ]),
  } as unknown as Block;

  return {
    cls: "take-sheet",
    eyebrow: ui("handout.sample"),
    title: doc.title,
    lead: doc.claim,
    blocks: [goal, kit],
  };
}

/* What a material's sheet says above the material: the kind, how long it runs
   and which levels of the goal it serves. Three facts a teacher decides from
   while holding a sheet away from the rest of the booklet. */
function eyebrow(item: KitItem, ui: Ui): string {
  if (isAbsent(item)) return ui(`kit.${item.kind}`);
  const mins = item.minutes ? ` · ${item.minutes} ${ui("lesson.minutes")}` : "";
  return `${ui(`kit.${item.kind}`)}${mins} · ${levels(item, ui)}`;
}

/* One material, one sheet: the kind above the title, the one-line claim under
   it, and then the material itself exactly as it is on screen.

   A recording prints as its script and nothing else — the player is not on
   paper, and neither is the sentence saying the recording has not been made
   yet, which is a fact about the portal and not about the lesson. What the
   reader is holding is said in the lead instead of in a box under the script:
   a script fills its page, and a box under a full page is a page of its own
   carrying two lines. */
function material(item: KitItem, t: Text, ui: Ui): GeneratedSheet | null {
  if (isAbsent(item) || !item.ready) return null;
  if (item.ready.as !== "blocks" && item.ready.as !== "audio") return null;

  const spoken = item.ready.as === "audio";
  return {
    cls: "take-sheet",
    eyebrow: eyebrow(item, ui),
    title: item.title,
    lead: spoken ? `${t(item.claim)} ${ui("handout.scriptsLead")}` : item.claim,
    blocks: item.ready.as === "blocks"
      ? item.ready.blocks
      : [{ k: "text", body: item.ready.script } as unknown as Block],
  };
}

export function lessonSheets(doc: Doc | undefined, pick: Pick, t: Text, ui: Ui): GeneratedSheet[] {
  if (!doc || !isLesson(doc)) return [];
  void pick;

  const sheets = [headSheet(doc, t, ui)];
  for (const item of doc.kit) {
    if (!printable(item)) continue;
    const one = material(item, t, ui);
    if (one) sheets.push(one);
  }
  return sheets;
}
